import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Heart, MessageSquare, ShoppingBag, Crown, Building2, Plus, Trash2, ClipboardList, Clock, CheckCircle2, XCircle, ListChecks, Store, Inbox, BadgeCheck, Download } from "lucide-react";
import { toast } from "sonner";
import { Layout, PageHeader } from "../components/Layout";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const Empty = ({ text, testid }) => <div data-testid={testid} className="text-center py-16 text-eon-muted border border-eon-border">{text}</div>;

const APP_STATUS_MAP = {
  pending: { icon: Clock, label: "Under Review", cls: "text-yellow-400 border-yellow-400/40" },
  approved: { icon: CheckCircle2, label: "Approved", cls: "text-green-400 border-green-400/40" },
  reserved: { icon: CheckCircle2, label: "Reserved", cls: "text-green-400 border-green-400/40" },
  waitlisted: { icon: ListChecks, label: "Waitlisted", cls: "text-yellow-400 border-yellow-400/40" },
  declined: { icon: XCircle, label: "Declined", cls: "text-red-400 border-red-400/40" },
};
const AppStatusBadge = ({ status }) => {
  const s = APP_STATUS_MAP[status] || APP_STATUS_MAP.pending;
  const Icon = s.icon;
  return <span data-testid={`app-badge-${status}`} className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider border px-3 py-1.5 ${s.cls}`}><Icon size={13} /> {s.label}</span>;
};

function ApplicationsSection({ applications, payDeposit }) {
  if (applications.length === 0) return <Empty text="No reservation applications yet. Apply from any puppy's page." />;
  return (
    <div className="space-y-3">
      {applications.map((a) => (
        <div key={a.id} className="eon-card p-5" data-testid={`my-application-${a.id}`}>
          <div className="flex flex-col md:flex-row md:items-center gap-4">
            <div className="flex-1">
              <div className="font-display font-bold text-lg">{a.dog_name}</div>
              <div className="text-xs text-eon-muted mt-1">Submitted {new Date(a.created_at).toLocaleDateString()} · {a.purpose}</div>
            </div>
            <AppStatusBadge status={a.status} />
            {a.status === "approved" && a.dog_id && (
              <button onClick={() => payDeposit(a.dog_id)} data-testid={`app-pay-deposit-${a.id}`}
                className="bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-eon-goldhover transition-colors">Pay Deposit</button>
            )}
          </div>
          {a.admin_note && <p className="text-xs text-eon-muted mt-3 border-t border-eon-border pt-3">Note from E.O.N: {a.admin_note}</p>}
        </div>
      ))}
    </div>
  );
}

function FavoritesSection({ favs }) {
  if (favs.dogs.length + favs.products.length === 0) return <Empty text="No favorites yet." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {favs.dogs.map((d) => (<Link key={d.id} to={`/dogs/${d.id}`} className="eon-card p-4 flex gap-4 items-center"><img src={d.image} alt="" className="w-16 h-16 object-cover" /><div><div className="eon-label">Dog · {d.breed}</div><div className="font-display font-bold">{d.name}</div><div className="text-sm text-eon-gold font-mono">${d.price?.toLocaleString()}</div></div></Link>))}
      {favs.products.map((p) => (<Link key={p.id} to={`/shop/${p.id}`} className="eon-card p-4 flex gap-4 items-center"><img src={p.image} alt="" className="w-16 h-16 object-cover" /><div><div className="eon-label">Product</div><div className="font-display font-bold">{p.name}</div><div className="text-sm text-eon-gold font-mono">${p.price?.toFixed(2)}</div></div></Link>))}
    </div>
  );
}

function InquiriesSection({ inquiries }) {
  if (inquiries.length === 0) return <Empty text="No inquiries sent." />;
  return (
    <div className="space-y-3">{inquiries.map((q) => (<div key={q.id} className="eon-card p-5"><div className="flex justify-between"><div className="eon-label">{q.subject_type.replace("_", " ")}</div><span className="text-xs text-eon-muted">{q.status}</span></div><p className="text-sm text-eon-text/80 mt-2">{q.message}</p></div>))}</div>
  );
}

function OrdersSection({ orders }) {
  if (orders.length === 0) return <Empty text="No orders yet." />;
  return (
    <div className="space-y-3">{orders.map((o) => (<div key={o.id} className="eon-card p-5 flex justify-between items-center"><div><div className="eon-label">{o.kind}</div><div className="text-sm text-eon-muted mt-1">{o.id}</div></div><div className="text-right"><div className="font-mono text-eon-gold">${o.amount?.toFixed(2)}</div><div className="text-xs text-eon-muted uppercase">{o.status}</div></div></div>))}</div>
  );
}

function PurchasesSection({ purchases }) {
  if (purchases.length === 0) return <Empty testid="purchases-empty" text="No digital purchases yet. Books, courses and guides you buy will appear here to download anytime." />;
  return (
    <div className="space-y-3" data-testid="purchases-list">
      {purchases.map((p) => (
        <div key={p.order_id} className="eon-card p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-testid={`purchase-${p.order_id}`}>
          <div className="flex items-start gap-3">
            <span className="w-10 h-10 rounded-xl bg-eon-elevated flex items-center justify-center shrink-0"><Download size={18} className="text-eon-gold" /></span>
            <div>
              <div className="font-display font-bold text-eon-text leading-snug">{p.name}</div>
              <div className="text-xs text-eon-muted mt-0.5">{p.purchased_at ? new Date(p.purchased_at).toLocaleDateString() : ""} · ${p.amount?.toFixed(2)}</div>
            </div>
          </div>
          <a href={p.download_url} target="_blank" rel="noreferrer" download data-testid={`purchase-download-${p.order_id}`}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-5 py-3 hover:bg-eon-goldhover transition-colors whitespace-nowrap">
            <Download size={15} /> Download
          </a>
        </div>
      ))}
    </div>
  );
}

function BreederListings({ myDogs, loadMyDogs }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="eon-heading text-2xl uppercase">My Listings</h3>
        <NewListingDialog onDone={loadMyDogs} />
      </div>
      {myDogs.length === 0 ? <Empty text="No listings yet. Create your first Boerboel listing." /> : (
        <div className="space-y-3">
          {myDogs.map((d) => (
            <div key={d.id} className="eon-card p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`my-listing-${d.id}`}>
              {d.image && <img src={d.image} alt="" className="w-14 h-14 object-cover" />}
              <div className="flex-1"><div className="font-display font-bold">{d.name}</div><div className="text-xs text-eon-muted">{d.breed} · ${d.price?.toLocaleString()} · {d.city}, {d.state}</div></div>
              <select value={d.status} onChange={async (e) => { await api.patch(`/breeder/dogs/${d.id}`, { status: e.target.value }); toast.success("Updated"); loadMyDogs(); }} data-testid={`my-listing-status-${d.id}`} className="bg-eon-bg border border-eon-border px-3 py-2 text-sm">
                {["available", "reserved", "sold", "coming_soon"].map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
              </select>
              <button onClick={async () => { await api.delete(`/breeder/dogs/${d.id}`); toast.success("Deleted"); loadMyDogs(); }} data-testid={`my-listing-delete-${d.id}`} className="text-eon-muted hover:text-red-400 p-2"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BreederSection({ stats, myDogs, loadMyDogs, bform, setBform, applyBreeder }) {
  if (!stats.breeder_profile) {
    return (
      <form onSubmit={applyBreeder} className="eon-card p-8 max-w-2xl space-y-4" data-testid="breeder-apply-form">
        <h3 className="font-display text-2xl font-bold">Apply as a Breeder</h3>
        <p className="text-sm text-eon-muted">Submit your kennel details for admin review.</p>
        <input required placeholder="Business / kennel name" value={bform.business_name} onChange={(e) => setBform({ ...bform, business_name: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-4 py-3 focus:border-eon-gold outline-none" data-testid="breeder-business" />
        <input required placeholder="Location (City, State)" value={bform.location} onChange={(e) => setBform({ ...bform, location: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-4 py-3 focus:border-eon-gold outline-none" data-testid="breeder-location" />
        <input placeholder="Breeds (comma separated)" value={bform.breeds} onChange={(e) => setBform({ ...bform, breeds: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-4 py-3 focus:border-eon-gold outline-none" />
        <textarea required rows={4} placeholder="About your kennel" value={bform.about} onChange={(e) => setBform({ ...bform, about: e.target.value })} className="w-full bg-eon-bg border border-eon-border px-4 py-3 focus:border-eon-gold outline-none" data-testid="breeder-about" />
        <button type="submit" className="bg-eon-gold text-white font-bold uppercase tracking-wider px-6 py-3 hover:bg-eon-goldhover transition-colors" data-testid="breeder-apply-submit">Submit Application</button>
      </form>
    );
  }
  return (
    <div className="space-y-8">
      <div className="eon-card p-8">
        <Building2 className="text-eon-gold" size={26} />
        <h3 className="font-display text-2xl font-bold mt-4">{stats.breeder_profile.business_name}</h3>
        <div className="mt-2 flex items-center gap-3"><span className={`eon-label ${stats.breeder_profile.approved ? "text-green-400" : "text-yellow-400"}`}>{stats.breeder_profile.approved ? "Approved" : "Pending Review"}</span><span className="eon-label">Tier: {stats.breeder_profile.tier}</span></div>
        <p className="text-sm text-eon-text/80 mt-4">{stats.breeder_profile.about}</p>
        {stats.breeder_profile.approved && <Link to={`/breeders/${stats.breeder_profile.id}`} className="inline-block mt-4 text-eon-gold text-sm">View public profile →</Link>}
      </div>
      {stats.breeder_profile.approved ? (
        <BreederListings myDogs={myDogs} loadMyDogs={loadMyDogs} />
      ) : (
        <div className="eon-card p-6 text-sm text-eon-muted">Your profile is awaiting admin approval. Once approved, you'll be able to create and manage Boerboel listings here.</div>
      )}
    </div>
  );
}

function LeadsSection({ leads }) {
  if (leads.length === 0) return <Empty text="No leads yet. When someone contacts one of your listings, it appears here." />;
  return (
    <div className="space-y-3">
      {leads.map((l) => (
        <div key={l.id} className="eon-card p-5" data-testid={`my-lead-${l.id}`}>
          <div className="flex justify-between items-start gap-3">
            <div>
              <div className="font-display font-bold text-eon-text">{l.name} <span className="text-xs text-eon-muted font-normal">· re: {l.service_name}</span></div>
              <div className="text-xs text-eon-muted mt-1">{l.email}{l.phone && ` · ${l.phone}`} · {new Date(l.created_at).toLocaleDateString()}</div>
            </div>
            <span className="text-[10px] font-bold uppercase tracking-widest bg-eon-elevated text-eon-gold rounded-full px-3 py-1">{l.status}</span>
          </div>
          <p className="text-sm text-eon-text/80 mt-3">{l.message}</p>
        </div>
      ))}
    </div>
  );
}

function BusinessSection({ myServices, loadMyServices }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", category: "Business Consulting", service_type: "business", location: "", description: "", phone: "", email: "", website: "" });
  const save = async () => {
    if (!f.name) { toast.error("Name is required"); return; }
    try { await api.post("/services", f); toast.success("Submitted for review"); setOpen(false); loadMyServices(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail) || "Could not submit"); }
  };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="eon-heading text-2xl text-eon-text">My Marketplace Listings</h3>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><button data-testid="new-service-btn" className="flex items-center gap-2 rounded-full bg-eon-gold text-white text-xs font-bold px-4 py-2.5 hover:bg-eon-goldhover transition-colors"><Plus size={16} /> New Listing</button></DialogTrigger>
          <DialogContent className="bg-white text-eon-text max-h-[85vh] overflow-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">New Marketplace Listing</DialogTitle></DialogHeader>
            <div className="space-y-3 pt-2">
              <input placeholder="Business / service name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={LIN} data-testid="service-name" />
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={LIN} />
                <select value={f.service_type} onChange={(e) => setF({ ...f, service_type: e.target.value })} className={LIN}><option value="business">Business (B2B)</option><option value="consumer">Consumer Service</option></select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input placeholder="Location" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} className={LIN} />
                <input placeholder="Website" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} className={LIN} />
              </div>
              <textarea rows={3} placeholder="Describe your service" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={LIN} data-testid="service-description" />
              <button onClick={save} className="w-full rounded-full bg-eon-gold text-white font-bold py-3 hover:bg-eon-goldhover transition-colors" data-testid="service-save">Submit for Review</button>
            </div>
          </DialogContent>
        </Dialog>
      </div>
      {myServices.length === 0 ? <Empty text="No listings yet. Create your first marketplace listing." /> : (
        <div className="space-y-3">
          {myServices.map((s) => (
            <div key={s.id} className="eon-card p-4 flex flex-col md:flex-row md:items-center gap-4" data-testid={`my-service-${s.id}`}>
              <Store size={20} className="text-eon-gold shrink-0" />
              <div className="flex-1">
                <div className="font-display font-bold text-eon-text flex items-center gap-1.5">{s.name}{s.verified && <BadgeCheck size={15} className="text-eon-gold" />}</div>
                <div className="text-xs text-eon-muted">{s.category} · {s.location || "—"} · {s.approved ? "Approved" : "Pending review"}{s.verified ? " · E.O.N Verified" : s.verification_requested ? " · Verification requested" : ""}</div>
              </div>
              {!s.verified && !s.verification_requested && (
                <button onClick={async () => { await api.post(`/my/services/${s.id}/request-verification`); toast.success("Verification requested"); loadMyServices(); }} data-testid={`request-verify-${s.id}`}
                  className="text-xs font-bold rounded-full border border-eon-border text-eon-text px-4 py-2 hover:border-eon-gold hover:text-eon-gold transition-colors flex items-center gap-1"><BadgeCheck size={14} /> Get Verified</button>
              )}
              <button onClick={async () => { await api.delete(`/my/services/${s.id}`); toast.success("Deleted"); loadMyServices(); }} data-testid={`my-service-delete-${s.id}`} className="text-eon-muted hover:text-red-500 p-2"><Trash2 size={18} /></button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function Dashboard() {
  const { user, loading, refresh } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({});
  const [favs, setFavs] = useState({ dogs: [], products: [] });
  const [inquiries, setInquiries] = useState([]);
  const [orders, setOrders] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [applications, setApplications] = useState([]);
  const [bform, setBform] = useState({ business_name: "", location: "", about: "", breeds: "" });
  const [myDogs, setMyDogs] = useState([]);
  const [myServices, setMyServices] = useState([]);
  const [myLeads, setMyLeads] = useState([]);

  const loadMyDogs = useCallback(() => api.get("/breeder/dogs").then((r) => setMyDogs(r.data)).catch(() => {}), []);
  const loadMyServices = useCallback(() => api.get("/my/services").then((r) => setMyServices(r.data)).catch(() => {}), []);
  const loadMyLeads = useCallback(() => api.get("/my/leads").then((r) => setMyLeads(r.data)).catch(() => {}), []);

  useEffect(() => {
    if (!loading && !user) nav("/login");
    if (user) {
      api.get("/dashboard/stats").then((r) => setStats(r.data));
      api.get("/favorites").then((r) => setFavs(r.data));
      api.get("/inquiries/mine").then((r) => setInquiries(r.data));
      api.get("/orders/mine").then((r) => setOrders(r.data));
      api.get("/my/purchases").then((r) => setPurchases(r.data)).catch(() => {});
      api.get("/applications/mine").then((r) => setApplications(r.data));
      if (user.role === "breeder") loadMyDogs();
      if (user.role === "business" || user.role === "vendor") { loadMyServices(); loadMyLeads(); }
    }
  }, [user, loading, nav, loadMyDogs, loadMyServices, loadMyLeads]);

  if (loading || !user) return <Layout><div className="max-w-[1400px] mx-auto px-5 py-32 text-eon-muted">Loading...</div></Layout>;

  const payDeposit = async (dogId) => {
    try {
      const { data } = await api.post("/payments/checkout", { kind: "deposit", dog_id: dogId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "Could not start checkout"); }
  };

  const applyBreeder = async (e) => {
    e.preventDefault();
    try {
      await api.post("/breeder/apply", { ...bform, breeds: bform.breeds.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Application submitted for review"); refresh();
      api.get("/dashboard/stats").then((r) => setStats(r.data));
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const CARDS = [
    { icon: Heart, label: "Favorites", val: stats.favorites },
    { icon: ClipboardList, label: "Applications", val: stats.applications },
    { icon: MessageSquare, label: "Inquiries", val: stats.inquiries },
    { icon: ShoppingBag, label: "Orders", val: stats.orders },
    { icon: Crown, label: "Membership", val: (stats.membership_tier || "free").toUpperCase() },
  ];

  return (
    <Layout>
      <PageHeader label={`${user.role} dashboard`} title={`Hello, ${user.name?.split(" ")[0]}`}
        subtitle={user.email} />
      <div className="max-w-[1400px] mx-auto px-5 lg:px-8 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-eon-border border border-eon-border mb-10">
          {CARDS.map((c) => (
            <div key={c.label} className="bg-eon-bg p-6" data-testid={`stat-${c.label.toLowerCase()}`}>
              <c.icon size={20} className="text-eon-gold" />
              <div className="font-display text-3xl font-black mt-3">{c.val ?? 0}</div>
              <div className="eon-label mt-1">{c.label}</div>
            </div>
          ))}
        </div>

        <Tabs defaultValue="applications">
          <TabsList className="bg-transparent border-b border-eon-border rounded-none w-full justify-start h-auto p-0 gap-6">
            {["applications", "favorites", "inquiries", "orders", "purchases", ...(user.role === "breeder" ? ["breeder"] : []), ...((user.role === "business" || user.role === "vendor") ? ["listings", "leads"] : [])].map((t) => (
              <TabsTrigger key={t} value={t} data-testid={`tab-${t}`}
                className="capitalize bg-transparent data-[state=active]:bg-transparent data-[state=active]:text-eon-gold data-[state=active]:border-b-2 data-[state=active]:border-eon-gold rounded-none px-0 pb-3 text-eon-muted">{t}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="applications" className="pt-8"><ApplicationsSection applications={applications} payDeposit={payDeposit} /></TabsContent>
          <TabsContent value="favorites" className="pt-8"><FavoritesSection favs={favs} /></TabsContent>
          <TabsContent value="inquiries" className="pt-8"><InquiriesSection inquiries={inquiries} /></TabsContent>
          <TabsContent value="orders" className="pt-8"><OrdersSection orders={orders} /></TabsContent>
          <TabsContent value="purchases" className="pt-8"><PurchasesSection purchases={purchases} /></TabsContent>

          {user.role === "breeder" && (
            <TabsContent value="breeder" className="pt-8">
              <BreederSection stats={stats} myDogs={myDogs} loadMyDogs={loadMyDogs} bform={bform} setBform={setBform} applyBreeder={applyBreeder} />
            </TabsContent>
          )}
          {(user.role === "business" || user.role === "vendor") && (
            <TabsContent value="listings" className="pt-8">
              <BusinessSection myServices={myServices} loadMyServices={loadMyServices} />
            </TabsContent>
          )}
          {(user.role === "business" || user.role === "vendor") && (
            <TabsContent value="leads" className="pt-8">
              <LeadsSection leads={myLeads} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Layout>
  );
}

const LIN = "w-full bg-eon-bg border border-eon-border px-3 py-2.5 text-sm focus:border-eon-gold outline-none";

function NewListingDialog({ onDone }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "", sex: "male", price: 3500, state: "", city: "", color: "Fawn", category: "puppy", work_type: "companion", age: "9 weeks", image: "", description: "", status: "available" });
  const save = async () => {
    try { await api.post("/breeder/dogs", { ...f, price: Number(f.price) }); toast.success("Listing created"); setOpen(false); onDone(); }
    catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><button data-testid="new-listing-btn" className="flex items-center gap-2 bg-eon-gold text-white text-xs font-bold uppercase tracking-wider px-4 py-2.5 hover:bg-eon-goldhover transition-colors"><Plus size={16} /> New Listing</button></DialogTrigger>
      <DialogContent className="bg-eon-surface border-eon-border text-eon-text max-h-[85vh] overflow-auto">
        <DialogHeader><DialogTitle className="font-display text-2xl">New Boerboel Listing</DialogTitle></DialogHeader>
        <div className="space-y-3 pt-2">
          <input placeholder="Dog name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className={LIN} data-testid="listing-name" />
          <div className="grid grid-cols-2 gap-3">
            <select value={f.sex} onChange={(e) => setF({ ...f, sex: e.target.value })} className={LIN}><option value="male">Male</option><option value="female">Female</option></select>
            <input type="number" placeholder="Price" value={f.price} onChange={(e) => setF({ ...f, price: e.target.value })} className={LIN} data-testid="listing-price" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <select value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className={LIN}><option value="puppy">Puppy</option><option value="adult">Adult</option></select>
            <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} className={LIN}>{["available","reserved","sold","coming_soon"].map((s) => <option key={s} value={s}>{s.replace("_"," ")}</option>)}</select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="City" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} className={LIN} data-testid="listing-city" />
            <input placeholder="State (e.g. TX)" value={f.state} onChange={(e) => setF({ ...f, state: e.target.value })} className={LIN} data-testid="listing-state" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Color" value={f.color} onChange={(e) => setF({ ...f, color: e.target.value })} className={LIN} />
            <input placeholder="Age (e.g. 9 weeks)" value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} className={LIN} />
          </div>
          <input placeholder="Image URL" value={f.image} onChange={(e) => setF({ ...f, image: e.target.value })} className={LIN} data-testid="listing-image" />
          <textarea placeholder="Description" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={LIN} data-testid="listing-description" />
          <button onClick={save} className="w-full bg-eon-gold text-white font-bold uppercase tracking-wider py-3 hover:bg-eon-goldhover transition-colors" data-testid="listing-save">Publish Listing</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
